/**
 * The units authorisation is expressed in.
 *
 * One list, consumed by the UI, by server functions and by the generator that
 * emits Firestore rules. Keeping it central is what stops those three from
 * quietly disagreeing, which is the usual way a permission bug reaches
 * production: the button is hidden, so nobody notices the API still allows it.
 */
export const MODULES = [
  'jobs',
  'schedule',
  'dailyLogs',
  'todos',
  'changeOrders',
  'selections',
  'warranties',
  'specifications',
  'timeClock',
  'files',
  'messaging',
  'costCodes',
  'catalog',
  'bids',
  /**
   * Requests for information (SCRUM-144, P8-11).
   *
   * A module of its own rather than folded into `changeOrders`, because an
   * RFI ties to change orders, selections **and** the schedule, and gating
   * it on any one of those would hide a question from the people most
   * likely to answer it. It is also the only module a **sub** holds at
   * `edit` while holding the things it links to at `view`: asking is what
   * a trade does with a drawing they cannot read.
   */
  'rfis',
  'estimates',
  'budget',
  'purchaseOrders',
  'bills',
  'invoices',
  'payments',
  'reports',
  'directory',
  'equipment',
  'settings',
  'integrations',
] as const;

export type Module = (typeof MODULES)[number];

/**
 * Ordered least to most capable. `none` is the floor rather than an absence,
 * so every module always has an explicit answer and a missing entry can never
 * be mistaken for permission.
 */
export const LEVELS = ['none', 'view', 'edit', 'admin'] as const;
export type Level = (typeof LEVELS)[number];

const LEVEL_RANK: Record<Level, number> = { none: 0, view: 1, edit: 2, admin: 3 };

export function levelAtLeast(actual: Level, required: Level): boolean {
  return LEVEL_RANK[actual] >= LEVEL_RANK[required];
}

/**
 * Capabilities that do not fit the none/view/edit/admin ladder.
 *
 * `seeBuilderCost` is the one with commercial consequences: it gates cost,
 * markup and margin on every financial record. A role can be allowed to read
 * a change order without being allowed to see what the builder pays for it.
 */
export const FLAGS = [
  'seeBuilderCost',
  'releaseFinancials',
  'approveShifts',
  'approveEstimates',
  'manageRoles',
  'manageMembers',
  'viewAllJobs',
  'exportData',
  'markReadyForPayment',
  'recordPayments',
  'editSchedulePublished',
  'deleteOthersRecords',
] as const;

export type Flag = (typeof FLAGS)[number];
